// src/features/team/pages/TeamPage.tsx
import {useTeamMembers} from "@/hooks/useTeamMembers";
import {useMemo} from "react";
import {MetaTags} from "@/hooks/MetaTags.tsx";

function CardSkeleton() {
    return (
        <li className="animate-pulse">
            <div className="aspect-[3/2] w-full rounded-2xl bg-gray-100"/>
            <div className="mt-6 h-5 w-2/3 rounded bg-gray-100"/>
            <div className="mt-4 h-4 w-full rounded bg-gray-100"/>
            <div className="mt-2 h-4 w-4/5 rounded bg-gray-100"/>
        </li>
    );
}

export default function TeamPage() {
    const {data, isLoading, isError} = useTeamMembers({
        page: 0,
        size: 24,
        sortBy: "name",
        sortOrder: "asc",
    });

    const items = useMemo(() => data?.items ?? [], [data]);

    return (
        <>
            <MetaTags
                title={"Kosmos 94.1 - Aanbieders"}
                description={"Ontmoet die span van Kosmos 94.1 se aanbieders"}
                url={"/op-die-lug/aanbieders"}
            />

            <section className="bg-white py-16 sm:py-22">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-4xl text-center">
                        <h2 className="text-4xl font-semibold tracking-tight text-blue-900 sm:text-5xl">
                            Aanbieders
                        </h2>
                        <p className="mt-6 text-lg/8 text-gray-600">
                            Ons span van aanbieders is passievol oor die nuus en die gemeenskap. Hulle bring 'n rykdom
                            aan
                            ervaring en kennis, en is toegewyd om akkurate en betroubare inligting te verskaf. Ontmoet
                            ons
                            span hieronder.
                        </p>

                        {isError && (
                            <p className="mt-4 text-sm text-red-600">
                                Kon nie die span laai nie. Probeer asseblief weer.
                            </p>
                        )}
                    </div>

                    <ul
                        role="list"
                        className="mx-auto mt-8 grid max-w-2xl grid-cols-1 gap-x-6 gap-y-20 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:gap-x-8 xl:col-span-2"
                    >
                        {isLoading &&
                            Array.from({length: 6}).map((_, i) => <CardSkeleton key={i}/>)
                        }

                        {!isLoading && items.length === 0 && (
                            <li className="col-span-full text-gray-600">
                                Geen spanlede beskikbaar nie.
                            </li>
                        )}

                        {!isLoading && items.map((person) => (
                            <li key={person.id}>
                                {/* Image */}
                                <img
                                    alt={person.name}
                                    src={person.imageUrl}
                                    className="aspect-[2/2] w-full rounded-2xl object-cover ring-1 ring-black/5"
                                    onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).src =
                                            "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='800' height='533'><rect width='100%' height='100%' fill='%23f3f4f6'/></svg>";
                                    }}
                                    loading="lazy"
                                />

                                {/* Name */}
                                <h3 className="mt-6 text-lg/8 font-semibold text-blue-900">
                                    {person.name}
                                </h3>

                                {/* Bio / Description */}
                                <p className="mt-4 text-base/7 text-gray-600">
                                    {person.description /* or person.bio if your API uses 'bio' */}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        </>
    );
}
